import { HttpClient } from '@angular/common/http';
import { CUSTOM_ASSETS_URL } from '@app/app.constants';
import { Branding } from '@app/portal/models/branding';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { Language } from '@app/translate/models/language';
import { TranslationMeta } from '@app/translate/models/translation-meta';
import { portalError } from '@app/utils/error/portal-error';
import { loggerFor } from '@app/utils/logger';
import { TranslateLoader } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export class PortalTranslateHttpLoaderService implements TranslateLoader {
  /**
   * Returns an array of language keys. This value is only available if the translations.json has
   * already been loaded (i.e. after the portal has been initialised).
   */
  get languageKeys(): string[] {
    return this.meta?.languages.map((language) => language.key) ?? [];
  }

  private get basePath() {
    return this.appBaseHref + this.prefix;
  }

  private get brandingPath() {
    return CUSTOM_ASSETS_URL + '/' + this.prefix;
  }

  private branding: Branding | null = null;
  private logger = loggerFor('PortalTranslateHttpService');
  private meta: TranslationMeta;

  constructor(
    private http: HttpClient,
    private portalDataService: PortalDataService,
    private appBaseHref: string,
    public prefix: string = 'assets/i18n/',
    public suffix: string = '.json'
  ) {}

  /**
   * Fetch all given languages
   * @param languageKeys
   */
  fetchAll(languageKeys: string[]): Observable<Language>[] {
    return languageKeys.map((key) => this.getLanguage(key));
  }

  /**
   * Fetch all given languages from the current branding
   * @param languageKeys
   */
  fetchBrandingTranslations(languageKeys: string[]): Observable<Language>[] {
    return languageKeys.map((key) => this.getLanguage(key, true).pipe(catchError(() => of({}))));
  }

  /**
   * Fetch translation metadata and map to language keys
   */
  fetchLanguageKeys(): Observable<string[]> {
    return this.portalDataService.translationMeta.data$.pipe(
      // Map to list of languages
      map((translationMeta: TranslationMeta) => translationMeta.languages.map((language) => language.key))
    );
  }

  getLanguage(lang: string, fromBranding: boolean = false): Observable<Language> {
    let basePath: string = this.basePath;
    let suffix: string = this.suffix;

    if (fromBranding === true) {
      basePath = this.brandingPath;
    } else {
      suffix += `?v=${environment.version}`;
    }

    return this.http.get<Language>(basePath + lang + suffix);
  }

  /**
   * Gets the translations from the server
   */
  getTranslation(lang: string): Observable<Language> {
    if (this.branding == null) {
      throw portalError({
        cause: new Error('Branding has not been set, translation cannot be loaded'),
        id: 'getTranslation',
      });
    }

    const dialect$ = this.getLanguage(lang, true).pipe(
      catchError(() => {
        this.logger.warn(`Couldn't find translations for branding ${this.branding.themeName}`);

        return of({});
      })
    );

    const lang$ = this.getLanguage(lang).pipe(catchError(() => of({})));

    return combineLatest([dialect$, lang$]).pipe(
      map(([dialect, lang]: [Language, Language]) => {
        return {
          ...lang,
          ...dialect,
        };
      })
    );
  }

  setBranding(branding: Branding): void {
    this.branding = branding;
  }
}
